import {
  TestDefinition,
  LabTestDefinition,
  ImagingTestDefinition,
  ProcedureTestDefinition,
  PhysicalExamTestDefinition,
  ReferralTestDefinition,
  GenericTestDefinition,
  RiskAssessmentTestDefinition,
  InterventionTestDefinition,
  TestType,
} from './procedures';

// Type guard functions
export function isLabTestDefinition(testDef: TestDefinition): testDef is LabTestDefinition {
  return testDef.testType === TestType.LAB;
}

export function isImagingTestDefinition(testDef: TestDefinition): testDef is ImagingTestDefinition {
  return testDef.testType === TestType.IMAGING;
}

export function isProcedureTestDefinition(testDef: TestDefinition): testDef is ProcedureTestDefinition {
  return testDef.testType === TestType.PROCEDURE;
}

export function isPhysicalExamTestDefinition(testDef: TestDefinition): testDef is PhysicalExamTestDefinition {
  return testDef.testType === TestType.PHYSICAL_EXAM;
}

export function isReferralTestDefinition(testDef: TestDefinition): testDef is ReferralTestDefinition {
  return testDef.testType === TestType.REFERRAL;
}

export function isGenericTestDefinition(testDef: TestDefinition): testDef is GenericTestDefinition {
  return testDef.testType === TestType.GENERIC;
}

export function isRiskAssessmentTestDefinition(testDef: TestDefinition): testDef is RiskAssessmentTestDefinition {
  return testDef.testType === TestType.RISK_ASSESSMENT;
}

export function isInterventionTestDefinition(testDef: TestDefinition): testDef is InterventionTestDefinition {
  return testDef.testType === TestType.INTERVENTION;
}

// Helper function to get display name for test types
export function getTestTypeDisplayName(testType: string): string {
  const displayNames: Record<string, string> = {
    'lab': 'Laboratory Test',
    'imaging': 'Imaging Study',
    'procedure': 'Procedure',
    'physical_exam': 'Physical Exam',
    'referral': 'Referral',
    'generic': 'General Order',
    'risk_assessment': 'Risk Assessment',
    'intervention': 'Intervention',
  };
  return displayNames[testType] || testType;
}

// Helper function to get required fields for each test type
export function getRequiredFieldsForTestType(testType: string): string[] {
  const baseFields = ['id', 'name', 'orderable', 'reportable', 'active', 'codeType', 'codeValue', 'testType'];
  
  const typeSpecificFields: Record<string, string[]> = {
    'lab': ['specimenType', 'containerType', 'analytes', 'abnormalFlags', 'referenceRangeLow', 'referenceRangeHigh', 'units'],
    'imaging': ['modality', 'bodyRegion', 'contrastRequired', 'dicomCompatible'],
    'procedure': ['performerRole', 'estimatedDuration', 'requiresConsent', 'location', 'instructions'],
    'physical_exam': ['bodySystems', 'formTemplateId', 'documentationRole', 'sections'],
    'referral': [
      'referralType', 'specialty', 'referralMethod', 'defaultReceivingFacility',
      'requiresAuth', 'defaultAuthInstructions', 'requiresNote', 'defaultNoteTemplateId',
      'attachmentsAllowed', 'trackingEnabled'
    ],
    'generic': ['responsibleRole', 'priority', 'instructions', 'routing'],
    'risk_assessment': ['assessmentTool', 'riskLevels', 'scoringMethod', 'isRequiredForEncounterType', 'formTemplateId', 'outcomeTriggers', 'abnormalFlags', 'referenceRangeLow', 'referenceRangeHigh'],
    'intervention': ['interventionType', 'targetCondition', 'deliveryMode', 'recommendedFrequency', 'documentationTemplateId', 'billingCode', 'instructions', 'requiresConsent', 'attachmentsAllowed', 'trackingEnabled'],
  };
  
  return [...baseFields, ...(typeSpecificFields[testType] || [])];
}

// Helper function to validate test definition based on type
export function validateTestDefinition(testDef: Partial<TestDefinition>): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!testDef.testType) {
    errors.push('testType is required');
    return { isValid: false, errors };
  }
  
  const requiredFields = getRequiredFieldsForTestType(testDef.testType);
  
  for (const field of requiredFields) {
    if (!(field in testDef) || testDef[field as keyof typeof testDef] === undefined || testDef[field as keyof typeof testDef] === null) {
      errors.push(`${field} is required for ${testDef.testType} tests`);
    }
  }
  
  // Type-specific validations
  if (testDef.testType === 'Lab' && testDef.analytes && !Array.isArray(testDef.analytes)) {
    errors.push('analytes must be an array for lab tests');
  }
  
  if (testDef.testType === 'Physical Exam') {
    if (testDef.bodySystems && !Array.isArray(testDef.bodySystems)) {
      errors.push('bodySystems must be an array for physical exam tests');
    }
    if (testDef.sections && !Array.isArray(testDef.sections)) {
      errors.push('sections must be an array for physical exam tests');
    }
  }
  
  if (testDef.testType === 'Referral') {
    if (testDef.defaultReceivingFacility && typeof testDef.defaultReceivingFacility !== 'object') {
      errors.push('defaultReceivingFacility must be an object for referral tests');
    }
  }
  
  if (testDef.testType === 'Generic') {
    if (testDef.routing && typeof testDef.routing !== 'object') {
      errors.push('routing must be an object for generic tests');
    }
  }
  
  if (testDef.testType === 'Risk Assessment') {
    if (testDef.riskLevels && !Array.isArray(testDef.riskLevels)) {
      errors.push('riskLevels must be an array for risk assessment tests');
    }
    if (testDef.isRequiredForEncounterType && !Array.isArray(testDef.isRequiredForEncounterType)) {
      errors.push('isRequiredForEncounterType must be an array for risk assessment tests');
    }
    if (testDef.outcomeTriggers && !Array.isArray(testDef.outcomeTriggers)) {
      errors.push('outcomeTriggers must be an array for risk assessment tests');
    }
  }
  
  return { isValid: errors.length === 0, errors };
}

// Helper function to create default test definition for a given type
export function createDefaultTestDefinition(testType: TestType): Partial<TestDefinition> {
  const base = {
    id: '',
    name: '',
    orderable: true,
    reportable: true,
    active: true,
    codeType: 'CPT4',
    codeValue: '',
    testType,
  };
  
  switch (testType) {
    case TestType.LAB:
      return {
        ...base,
        specimenType: '',
        containerType: '',
        analytes: [],
        abnormalFlags: '',
        referenceRangeLow: '',
        referenceRangeHigh: '',
        units: '',
      } as Partial<LabTestDefinition>;
      
    case TestType.IMAGING:
      return {
        ...base,
        modality: '',
        bodyRegion: '',
        contrastRequired: false,
        dicomCompatible: true,
      } as Partial<ImagingTestDefinition>;
      
    case TestType.PROCEDURE:
      return {
        ...base,
        performerRole: '',
        estimatedDuration: 0,
        requiresConsent: false,
        location: '',
        instructions: '',
      } as Partial<ProcedureTestDefinition>;
      
    case TestType.PHYSICAL_EXAM:
      return {
        ...base,
        bodySystems: [],
        formTemplateId: '',
        documentationRole: '',
        sections: [],
      } as Partial<PhysicalExamTestDefinition>;
      
    case TestType.REFERRAL:
      return {
        ...base,
        referralType: '',
        specialty: '',
        referralMethod: '',
        defaultReceivingFacility: {
          name: '',
          npi: '',
          directAddress: '',
        },
        requiresAuth: false,
        defaultAuthInstructions: '',
        requiresNote: false,
        defaultNoteTemplateId: '',
        attachmentsAllowed: false,
        trackingEnabled: false,
      } as Partial<ReferralTestDefinition>;
      
    case TestType.GENERIC:
      return {
        ...base,
        responsibleRole: '',
        priority: 'Routine',
        instructions: '',
        routing: {
          targetInbox: '',
          notifyProvider: false,
        },
      } as Partial<GenericTestDefinition>;
      
    case TestType.RISK_ASSESSMENT:
      return {
        ...base,
        assessmentTool: '',
        riskLevels: [],
        scoringMethod: '',
        isRequiredForEncounterType: [],
        formTemplateId: '',
        outcomeTriggers: [],
        abnormalFlags: '',
        referenceRangeLow: '',
        referenceRangeHigh: '',
      } as Partial<RiskAssessmentTestDefinition>;
      
    case TestType.INTERVENTION:
      return {
        ...base,
        interventionType: '',
        targetCondition: '',
        deliveryMode: '',
        recommendedFrequency: '',
        documentationTemplateId: '',
        billingCode: '',
        instructions: '',
        requiresConsent: false,
        attachmentsAllowed: false,
        trackingEnabled: false,
      } as Partial<InterventionTestDefinition>;
      
    case TestType.MEDICATION:
      return {
        ...base,
        responsibleRole: '',
        priority: 'Routine',
        instructions: '',
        routing: {
          targetInbox: '',
          notifyProvider: false,
        },
      } as Partial<GenericTestDefinition>; // Use generic structure for medications
      
    default:
      return base as Partial<TestDefinition>;
  }
}

// Helper to get field labels for UI forms
export function getFieldLabelsForTestType(testType: string): Record<string, string> {
  const baseLabels = {
    id: 'ID',
    name: 'Name',
    orderable: 'Orderable',
    reportable: 'Reportable',
    active: 'Active',
    codeType: 'Code Type',
    codeValue: 'Code Value',
    testType: 'Test Type',
  };
  
  const typeSpecificLabels: Record<string, Record<string, string>> = {
    'Lab': {
      specimenType: 'Specimen Type',
      containerType: 'Container Type',
      analytes: 'Analytes',
      abnormalFlags: 'Abnormal Flags',
      referenceRangeLow: 'Reference Range Low',
      referenceRangeHigh: 'Reference Range High',
      units: 'Units',
    },
    'Imaging': {
      modality: 'Modality',
      bodyRegion: 'Body Region',
      contrastRequired: 'Contrast Required',
      dicomCompatible: 'DICOM Compatible',
    },
    'Procedure': {
      performerRole: 'Performer Role',
      estimatedDuration: 'Estimated Duration (minutes)',
      requiresConsent: 'Requires Consent',
      location: 'Location',
      instructions: 'Instructions',
    },
    'Physical Exam': {
      bodySystems: 'Body Systems',
      formTemplateId: 'Form Template ID',
      documentationRole: 'Documentation Role',
      sections: 'Exam Sections',
    },
    'Referral': {
      referralType: 'Referral Type',
      specialty: 'Specialty',
      referralMethod: 'Referral Method',
      defaultReceivingFacility: 'Default Receiving Facility',
      requiresAuth: 'Requires Authorization',
      defaultAuthInstructions: 'Default Auth Instructions',
      requiresNote: 'Requires Note',
      defaultNoteTemplateId: 'Default Note Template ID',
      attachmentsAllowed: 'Attachments Allowed',
      trackingEnabled: 'Tracking Enabled',
    },
    'Generic': {
      responsibleRole: 'Responsible Role',
      priority: 'Priority',
      instructions: 'Instructions',
      routing: 'Routing',
    },
    'Risk Assessment': {
      assessmentTool: 'Assessment Tool',
      riskLevels: 'Risk Levels',
      scoringMethod: 'Scoring Method',
      isRequiredForEncounterType: 'Required for Encounter Types',
      formTemplateId: 'Form Template ID',
      outcomeTriggers: 'Outcome Triggers',
      abnormalFlags: 'Abnormal Flags',
      referenceRangeLow: 'Reference Range Low',
      referenceRangeHigh: 'Reference Range High',
    },
    'Intervention': {
      interventionType: 'Intervention Type',
      targetCondition: 'Target Condition',
      deliveryMode: 'Delivery Mode',
      recommendedFrequency: 'Recommended Frequency',
      documentationTemplateId: 'Documentation Template ID',
      billingCode: 'Billing Code',
      instructions: 'Instructions',
      requiresConsent: 'Requires Consent',
      attachmentsAllowed: 'Attachments Allowed',
      trackingEnabled: 'Tracking Enabled',
    },
  };
  
  return { ...baseLabels, ...(typeSpecificLabels[testType] || {}) };
} 